import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Maximize2, Minimize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PitchDeckPasswordForm, { getPitchDeckSession, clearPitchDeckSession } from './PitchDeckPasswordForm';

interface PitchDeckModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PitchDeckModal = ({ open, onOpenChange }: PitchDeckModalProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);

  const slides = [
    {
      src: '/pitch-deck/slide-01.jpg',
      title: 'Integrated Special Purpose AI',
      description: 'The Wisdom of the Workforce'
    },
    {
      src: '/pitch-deck/slide-02.jpg',
      title: 'The Founders',
      description: 'This is who you are investing in with iSPAI'
    },
    {
      src: '/pitch-deck/slide-03.jpg',
      title: 'The Problem',
      description: 'Knowledge Entropy (loss) has been a struggle for decades'
    },
    {
      src: '/pitch-deck/slide-04.jpg',
      title: 'The Solution',
      description: 'A platform to create, consume, and monetize knowledge for AI'
    },
    {
      src: '/pitch-deck/slide-05.jpg',
      title: 'The Product Roadmap',
      description: 'We have a well-defined path forward to iterate PMF with customers'
    },
    {
      src: '/pitch-deck/slide-06.jpg',
      title: 'The Launch Plan',
      description: 'We are in beta testing now with early users'
    },
    {
      src: '/pitch-deck/slide-07.jpg',
      title: 'The Angel Rounds',
      description: 'This raise positions us for validated PMF, first revenue, and a VC-led Seed in 2026'
    },
    {
      src: '/pitch-deck/slide-08.jpg',
      title: 'The Capital Roadmap',
      description: 'From validation to repeatable ARR'
    },
    {
      src: '/pitch-deck/slide-09.png',
      title: 'The Revenue Engine & Replication Logic',
      description: 'How we scale from $5M to $50M+ ARR'
    },
    {
      src: '/pitch-deck/slide-10.jpg',
      title: 'The Extended Team',
      description: 'Proven ability to execute, and 100% aligned on our vision'
    },
    {
      src: '/pitch-deck/slide-11.jpg',
      title: 'The Pain',
      description: 'AI can replace us, or it can represent us'
    },
    {
      src: '/pitch-deck/slide-12.jpg',
      title: 'The Possibility',
      description: 'Define a new role for humans in an AI-driven future'
    },
    {
      src: '/pitch-deck/slide-13.jpg',
      title: 'Thank You',
      description: 'Contact information'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const toggleFullScreen = () => {
    setIsFullScreen((prev) => !prev);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') nextSlide();
    if (e.key === 'ArrowLeft') prevSlide();
    if (e.key === 'Escape' && isFullScreen) {
      setIsFullScreen(false);
    } else if (e.key === 'Escape') {
      onOpenChange(false);
    }
    if (e.key === 'f' || e.key === 'F') toggleFullScreen();
  };

  React.useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open]);

  React.useEffect(() => {
    if (open) {
      setCurrentSlide(0);
      setIsFullScreen(false);
      
      // Check for existing session when modal opens
      const existingSession = getPitchDeckSession();
      if (existingSession) {
        setIsUnlocked(true);
      } else {
        setIsUnlocked(false);
      }
    }
  }, [open]);

  const handleUnlock = () => {
    setIsUnlocked(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`p-0 border-0 bg-transparent ${!isUnlocked ? 'max-w-md w-full h-auto' : isFullScreen ? 'max-w-none w-screen h-screen rounded-none' : 'max-w-6xl w-full h-[90vh]'}`}>
        {!isUnlocked ? (
          <div className="rounded-lg overflow-hidden">
            <PitchDeckPasswordForm onUnlock={handleUnlock} />
          </div>
        ) : (
        <div className={`relative w-full h-full bg-black/95 overflow-hidden ${isFullScreen ? '' : 'rounded-lg'}`}>
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
            <div className="flex justify-between items-center text-white">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold">iSPAI Pitch Deck</h3>
                <span className="text-sm text-gray-300">
                  {currentSlide + 1} of {slides.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleFullScreen}
                  className="text-white hover:bg-white/20"
                  title={isFullScreen ? 'Exit full screen (F)' : 'Full screen (F)'}
                >
                  {isFullScreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="relative w-full h-[calc(100%-4rem)] flex items-center justify-center p-4 pt-16 pb-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="relative flex items-center justify-center w-full h-full"
              >
                <img
                  src={slides[currentSlide].src}
                  alt={slides[currentSlide].title}
                  className={`max-w-full object-contain rounded-lg shadow-2xl ${isFullScreen ? 'max-h-[calc(100vh-8rem)]' : 'max-h-[calc(90vh-10rem)]'}`}
                />
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <Button
              variant="ghost"
              size="sm"
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 text-white hover:bg-black/80 h-12 w-12 rounded-full shadow-lg"
              disabled={slides.length <= 1}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 text-white hover:bg-black/80 h-12 w-12 rounded-full shadow-lg"
              disabled={slides.length <= 1}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>

          {/* Slide Indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 rounded-full px-4 py-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide
                    ? 'bg-white scale-125'
                    : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>

        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PitchDeckModal;